import { useEffect, useRef, type RefObject } from 'react';
import {
  SEQUENCE_SEPARATOR,
  armSequence,
  armedSequence,
  armsSequence,
  disarmSequence,
  isBoundSequence,
  markSequenceAnswered,
  registerHotkeyMap,
} from './hotkey-registry';

export type HotkeyHandler = (event: KeyboardEvent) => void;

export interface HotkeyBinding {
  handler: HotkeyHandler;
  /**
   * Let the browser's auto-repeat drive the handler while the key is held.
   *
   * Off by default, because that is what a command wants: holding `mod+k`
   * must not reopen the palette thirty times a second. On for movement —
   * holding `j` is how a person crosses a long list.
   */
  allowRepeat?: boolean;
}

/** A binding is a handler, or a handler plus its options. */
export type HotkeyEntry = HotkeyHandler | HotkeyBinding;

/** Keys are `'mod+k'`, `'shift+?'`, or a sequence like `'g l'`. */
export type HotkeyMap = Record<string, HotkeyEntry>;

export interface UseHotkeysOptions {
  enabled?: boolean;
  /** Listen on this element instead of the document. */
  scopeRef?: RefObject<HTMLElement | null>;
  /** Fire even when the user is typing. Off by default per ARCHITECTURE §9. */
  enableInFormFields?: boolean;
  /** How long a multi-key sequence stays armed. */
  sequenceTimeout?: number;
}

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return target.closest('[role="textbox"], [contenteditable="true"]') !== null;
}

function normalize(event: KeyboardEvent): string {
  const printable = event.key.length === 1;
  const parts: string[] = [];
  if (event.metaKey || event.ctrlKey) parts.push('mod');
  if (event.altKey) parts.push('alt');
  // Shift is already baked into a printable character: on a Latin American
  // layout `/` *is* Shift+7, and binding it as `shift+/` would never fire.
  if (event.shiftKey && !printable) parts.push('shift');
  parts.push(event.key.toLowerCase());
  return parts.join('+');
}

function toBinding(entry: HotkeyEntry): HotkeyBinding {
  return typeof entry === 'function' ? { handler: entry } : entry;
}

const findSequence = (entries: [string, HotkeyEntry][], sequence: string) =>
  entries.find(([binding]) => binding.toLowerCase() === sequence);

/**
 * Scoped keyboard shortcuts.
 *
 * Handlers live in a ref so a caller can pass an inline object literal without
 * re-binding the listener on every render.
 */
export function useHotkeys(map: HotkeyMap, options: UseHotkeysOptions = {}): void {
  const {
    enabled = true,
    scopeRef,
    enableInFormFields = false,
    sequenceTimeout = 1200,
  } = options;

  const handlers = useRef(map);
  useEffect(() => {
    handlers.current = map;
  });

  // Registered while mounted so the other listeners can see these bindings; the
  // armed sequence is shared, and a listener that cannot see it eats the tail.
  useEffect(() => {
    if (!enabled) return;
    return registerHotkeyMap(handlers);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    const target: HTMLElement | Document = scopeRef?.current ?? document;

    const onKeyDown = (event: Event) => {
      const keyEvent = event as KeyboardEvent;
      if (!enableInFormFields && isTyping(keyEvent.target)) return;

      const combo = normalize(keyEvent);
      const entries = Object.entries(handlers.current);
      const findExact = () => entries.find(([binding]) => binding.toLowerCase() === combo);

      if (keyEvent.repeat) {
        // A held key still belongs to whoever bound it — swallow it so the
        // browser does not act on it — but only `allowRepeat` bindings run.
        // Repeats never arm a sequence: holding `g` must not queue jumps.
        const held = findExact();
        if (!held) return;
        keyEvent.preventDefault();
        const binding = toBinding(held[1]);
        if (binding.allowRepeat) binding.handler(keyEvent);
        return;
      }

      // A sequence armed by *this* keystroke is not answered by it.
      const pending = armedSequence();
      if (pending && pending.event !== keyEvent) {
        const sequence = `${pending.prefix}${SEQUENCE_SEPARATOR}${combo}`;
        if (isBoundSequence(sequence)) {
          // The key is the tail of `g c`, not a bare `c`: only the listener that
          // binds the jump answers, and nobody falls through to the single key —
          // whatever order the listeners happen to be registered in.
          const match = pending.answeredBy === keyEvent ? undefined : findSequence(entries, sequence);
          keyEvent.preventDefault();
          if (match) {
            markSequenceAnswered(keyEvent);
            toBinding(match[1]).handler(keyEvent);
          }
          return;
        }
        // Nobody claims this pair: a stray `g` must not eat the next keystroke.
        disarmSequence();
      }

      const exact = findExact();
      if (exact) {
        keyEvent.preventDefault();
        toBinding(exact[1]).handler(keyEvent);
        return;
      }

      if (armsSequence(combo)) armSequence(combo, keyEvent, sequenceTimeout);
    };

    target.addEventListener('keydown', onKeyDown);
    return () => {
      target.removeEventListener('keydown', onKeyDown);
    };
  }, [enabled, scopeRef, enableInFormFields, sequenceTimeout]);
}
